import React from 'react';
import { useEffect, useState } from "react"
import { Modal, Table, Button, Space, Typography, Tag, Dropdown, Spin, Popconfirm } from 'antd';
import { CloseOutlined, DownOutlined, EyeOutlined } from '@ant-design/icons';
import PropTypes from "prop-types" // Import PropTypes
import dayjs from "dayjs";
import {onShowSizeChange,itemRender}from  '../Pagination'
import { Link } from "react-router-dom";
import { getAppointmentReports } from "../../services/lab/lab_appointment";

const { Title, Text } = Typography;

const ReportHistoryModal = ({ isOpenReportHistory, onClose, patientData }) => {

  const [loading, setLoading] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [previewFile, setPreviewFile] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const fetchLabAppointments = async () => {
    try {
      setLoading(true);
      const result = await getAppointmentReports(
        String(patientData?.id)
      );

      if (result.success)
      {
        // console.log("fetchLabAppointments Data:", result);
        setReportHistory(result.data);
      }
      else
      {
        console.error("Error:", result.error || "Failed to fetch Patient Reports");
        setReportHistory([]);
      }
    }
    catch (err)
    {
      console.error("Exception:", err.message);
      setReportHistory([]);
    }
    finally
    {
      setLoading(false);
    }
  };
    
  useEffect(()=>{
    if( isOpenReportHistory )
    {
      fetchLabAppointments();
    }
  },[isOpenReportHistory]);

  const handlePreview = (fileUrl) => {
    setPreviewFile(fileUrl);
    setPreviewOpen(true);
  };

  return (
    <>
      <Modal
        open={isOpenReportHistory}
        onCancel={onClose}
        footer={null}
        width={1200}
        closable={false} // Custom header close button
        style={{ padding: 0 }}
      >
        {/* Top Patient Header Bar */}
        <div style={{ 
          padding: '12px 24px', 
          borderBottom: '1px solid #f0f0f0', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>{patientData?.full_name}</Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>{patientData?.gender}, {patientData?.age}</Text>
          </div>
          <Button icon={<CloseOutlined />} onClick={onClose}>Close</Button>
        </div>

        {/* Main Content Area */}
        <div style={{ padding: '24px 40px' }}>
          {/* Report History Title and Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <Title level={3} style={{ margin: 0 }}>Report History</Title>
          </div>

          {/* Table Section */}
          {loading ? (
            <div className="w-100 p-4 d-flex align-items-center justify-content-center">
              <Spin size="large" />
            </div>
          ) : (
            <Table 
              dataSource={reportHistory}
              columns={[
                {
                  title: 'Report File', 
                  dataIndex: 'file_name', 
                  key: 'report_file', 
                  render: (text, record) => (
                    <>{record.report_file}</>
                  ),
                },
                { 
                  title: 'Upload Date', 
                  dataIndex: 'created_at', 
                  key: 'created_at', 
                  render: (text, record) => (
                    <>{dayjs(record.created_at).format("DD MMM YYYY hh:mm A")}</>
                  ),
                },
                { 
                    title: 'Action', 
                    key: 'action', 
                    render: (_, record) => (
                        <Space>
                            <Button size="small" icon={<EyeOutlined />} onClick={() => handlePreview(record?.report_file_url)}>Preview</Button>
                        </Space>
                    )
                }
            ]}
              //pagination={{ position: ['bottomCenter'], current: 1, total: 1 }}
              pagination={{
                total: reportHistory.length,
                showTotal: (total, range) => (`Showing ${range[0]} to ${range[1]} of ${total} entries`),
                itemRender: itemRender,
              }}
              rowClassName={() => 'report-row'}
            />
          )}
        </div>
      </Modal>

      {/* Report Preview Modal */}
      <Modal
        title="Report Preview"
        centered
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[<Button key="close" onClick={() => setPreviewOpen(false)}>Close</Button>]}
        width={1000}
      >
        <div style={{ height: '70vh' }}>
          {previewFile ? (
            <div>
              {previewFile.endsWith('.pdf') ? (
                <iframe src={previewFile} width="100%" height="100%" title="Report" />
              ) : (
                <img src={previewFile} alt="Report Preview" style={{ width: '100%', objectFit: 'contain' }} />
              )}
            </div>
          ) : (
            <div className="h-100 d-flex align-items-center justify-content-center">No preview available</div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ReportHistoryModal;
ReportHistoryModal.propTypes = {
    isOpenReportHistory: PropTypes.bool.isRequired, // isOpen must be a boolean and is required
    onClose: PropTypes.func.isRequired, // onClose must be a function and is required
    patientData: PropTypes.object.isRequired,
  }